import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { GETAPI } from "../../api/Api";
import { ApiConstants } from "../../constants/ApiConstants";
import { Routes } from "../../constants/RouteConstants";
import Spinner from "../../components/Spinner";
import { Config } from "../../config/Config";
import { OfflineSubscriptionUserStatus } from "../../utils/subscriptionAccess";
import SubscriptionBankSummary from "./SubscriptionBankSummary";
import ActiveSubscriptionOverview from "./ActiveSubscriptionOverview";
import SubscriptionPlanFeaturesList from "./SubscriptionPlanFeaturesList";

const pickErrorMessage = (res: any): string => {
  const m = res?.message;
  if (typeof m === "string" && m.trim()) return m;
  if (m && typeof m === "object" && typeof m.message === "string") return m.message;
  return "";
};

const formatDt = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString() : "—";

const isPastDate = (v: string | null | undefined): boolean => {
  if (!v) return false;
  const t = new Date(v).getTime();
  return Number.isFinite(t) && t < Date.now();
};

function subscriptionUserStatusLabel(st: number | undefined): string {
  switch (st) {
    case OfflineSubscriptionUserStatus.NONE:
      return "None";
    case OfflineSubscriptionUserStatus.PENDING_VERIFY:
      return "Pending verification";
    case OfflineSubscriptionUserStatus.ACTIVE:
      return "Active";
    case OfflineSubscriptionUserStatus.REJECTED:
      return "Rejected";
    case OfflineSubscriptionUserStatus.EXPIRED:
      return "Expired";
    case OfflineSubscriptionUserStatus.SUSPENDED:
      return "Suspended";
    default:
      return "—";
  }
}

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const profileData = useSelector((state: any) => state.GetProfileState);
  const profileUser = profileData?.data?.data || Config?.USER_DETAILS;
  const profileSuspended =
    Number(profileUser?.offline_subscription_status) ===
    OfflineSubscriptionUserStatus.SUSPENDED;
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<any[]>([]);
  const [salonSub, setSalonSub] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bank, setBank] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [pendingRequest, setPendingRequest] = useState<any>(null);
  const [hasPendingUpgrade, setHasPendingUpgrade] = useState(false);
  const [txHistory, setTxHistory] = useState<any[]>([]);

  const roleId = Config?.USER_DETAILS?.role_id;
  const isOwner = Number(roleId) === 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      const [plansRes, bankRes, histRes]: any[] = await Promise.all([
        GETAPI(ApiConstants.OfflineSubscriptionPlans, {}, false),
        GETAPI(ApiConstants.OfflineSubscriptionBankDetails, {}, false),
        GETAPI(ApiConstants.OfflineSubscriptionMyRequests, {}, false),
      ]);
      if (cancelled) return;
      if (plansRes?.status === true && Array.isArray(plansRes?.data?.plans)) {
        setPlans(plansRes.data.plans);
        setSalonSub(plansRes.data.salon_subscription ?? null);
        setCurrentPlan(plansRes.data.current_plan ?? null);
        setPendingRequest(plansRes.data.pending_request ?? null);
        {
          const sub = plansRes.data.salon_subscription ?? null;
          const pend = plansRes.data.pending_request ?? null;
          const hp =
            plansRes.data.has_pending_upgrade === true ||
            (!!pend &&
              Number(sub?.offline_subscription_status) ===
                OfflineSubscriptionUserStatus.ACTIVE);
          setHasPendingUpgrade(hp);
        }
        setLoadError(null);
      } else {
        setPlans([]);
        setSalonSub(null);
        setCurrentPlan(null);
        setPendingRequest(null);
        setHasPendingUpgrade(false);
        setLoadError(
          pickErrorMessage(plansRes) ||
            "Unable to load subscription plans. Please try again or contact support.",
        );
      }
      if (bankRes?.status === true && bankRes?.data) {
        setBank(bankRes.data);
      } else {
        setBank(null);
      }
      if (histRes?.status === true && Array.isArray(histRes?.data?.requests)) {
        setTxHistory(histRes.data.requests);
      } else {
        setTxHistory([]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goPurchase = (planId: number) => {
    navigate(`${Routes.SubscriptionPurchase}?plan_id=${planId}`);
  };

  const pendingVerify =
    salonSub?.offline_subscription_status ===
    OfflineSubscriptionUserStatus.PENDING_VERIFY;

  const subSt = salonSub?.offline_subscription_status;
  const displayStatus =
    subSt !== OfflineSubscriptionUserStatus.SUSPENDED &&
    isPastDate(salonSub?.offline_subscription_expires_at)
      ? OfflineSubscriptionUserStatus.EXPIRED
      : subSt;
  const isSuspended =
    subSt === OfflineSubscriptionUserStatus.SUSPENDED || profileSuspended;
  const isActive = subSt === OfflineSubscriptionUserStatus.ACTIVE;
  const curPrice = currentPlan ? Number(currentPlan.price) : 0;

  const planButtonState = (p: any) => {
    if (isSuspended) {
      return {
        disabled: true,
        labelId: "subscription_suspended_no_purchase",
        labelDefault: "Account suspended",
      };
    }
    const isCurrent = isActive && currentPlan && p.id === currentPlan.id;
    const lowerOrEqualTier =
      isActive && currentPlan && Number(p.price) <= curPrice && !isCurrent;
    const upgradeAlreadyQueued =
      hasPendingUpgrade && Number(p.price) > curPrice;
    const disabled =
      pendingVerify ||
      upgradeAlreadyQueued ||
      !isOwner ||
      isCurrent ||
      lowerOrEqualTier;
    let labelId = "purchase_subscription";
    let labelDefault = "Purchase subscription";
    if (isCurrent) {
      labelId = "subscription_plan_current";
      labelDefault = "Current plan";
    } else if (isActive && Number(p.price) > curPrice) {
      labelId = "subscription_plan_upgrade";
      labelDefault = "Upgrade";
      if (upgradeAlreadyQueued) {
        labelId = "subscription_upgrade_pending_queue";
        labelDefault = "Upgrade pending review";
      }
    }
    return { disabled, labelId, labelDefault };
  };

  return (
    <div className="col-12 col-lg-9">
      <section className="innerBanner">
        <div className="BannerText container py-5">
          <h1>
            <FormattedMessage id="subscription_plans_title" defaultMessage="Subscription plans" />
          </h1>
          <p className="mb-0">
            <FormattedMessage
              id="subscription_plans_subtitle"
              defaultMessage="Choose a plan and complete offline payment to activate your salon."
            />
          </p>
        </div>
      </section>
      <section className="py-4 px-3 px-lg-4">
        {loading ? (
          <Spinner />
        ) : (
          <div className="row g-4 align-items-start">
            <div className="col-12 col-lg-8">
              {!loadError && isSuspended && (
                <div className="alert alert-danger border-0 shadow-sm mb-4" role="alert">
                  <h2 className="h6 alert-heading mb-2">
                    <FormattedMessage
                      id="subscription_suspended_title"
                      defaultMessage="Your salon account is suspended"
                    />
                  </h2>
                  <p className="mb-2 small">
                    <FormattedMessage
                      id="subscription_suspended_body"
                      defaultMessage="An administrator has suspended your subscription. Services management, staff management, and offline bookings stay locked until access is restored; you can still use the rest of your account. Please contact support if you have questions."
                    />
                  </p>
                  <Link to="/contact-support" className="btn btn-sm btn-outline-danger">
                    <FormattedMessage id="customer_support" defaultMessage="Customer support" />
                  </Link>
                </div>
              )}
              {loadError && (
                <div className="alert alert-warning" role="alert">
                  {loadError}
                </div>
              )}
              {!isOwner && !loadError && (
                <div className="alert alert-secondary" role="alert">
                  <FormattedMessage
                    id="subscription_plans_staff_note"
                    defaultMessage="Only the salon owner can purchase a subscription. You can review the plans below."
                  />
                </div>
              )}

              {!loadError &&
                isActive &&
                currentPlan &&
                salonSub &&
                !isSuspended && (
                <ActiveSubscriptionOverview
                  plan={currentPlan}
                  startedAt={salonSub.offline_subscription_started_at}
                  expiresAt={salonSub.offline_subscription_expires_at}
                  formatDt={formatDt}
                />
              )}

              {!loadError && hasPendingUpgrade && pendingRequest && !isSuspended && (
                <div className="alert alert-warning border-0 shadow-sm mb-4" role="status">
                  <h2 className="h6 alert-heading mb-2">
                    <FormattedMessage
                      id="subscription_upgrade_pending_title"
                      defaultMessage="Upgrade payment under review"
                    />
                  </h2>
                  <p className="small mb-2">
                    <FormattedMessage
                      id="subscription_upgrade_pending_body"
                      defaultMessage="Your current subscription stays fully active below. When an administrator approves this payment, your plan will change to the requested upgrade."
                    />
                  </p>
                  <p className="small mb-1">
                    <FormattedMessage id="transaction_id" defaultMessage="Transaction ID" />:{" "}
                    {pendingRequest.transaction_id}
                  </p>
                  {pendingRequest.requested_plan && (
                    <p className="small mb-1">
                      <FormattedMessage
                        id="subscription_requested_plan"
                        defaultMessage="Requested plan"
                      />
                      : {pendingRequest.requested_plan.name}
                      {pendingRequest.requested_plan.price != null && (
                        <> (€{Number(pendingRequest.requested_plan.price).toFixed(2)})</>
                      )}
                    </p>
                  )}
                  {pendingRequest.previous_plan && (
                    <p className="small text-muted mb-0">
                      <FormattedMessage
                        id="subscription_upgrade_from"
                        defaultMessage="Upgrading from"
                      />
                      : {pendingRequest.previous_plan.name}
                    </p>
                  )}
                </div>
              )}

              {!loadError &&
                salonSub &&
                !(isActive && currentPlan && !isSuspended) &&
                (pendingRequest ||
                  currentPlan ||
                  subSt !== OfflineSubscriptionUserStatus.NONE ||
                  isSuspended) && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body">
                    <h2 className="h6 card-title">
                      <FormattedMessage
                        id="subscription_details_title"
                        defaultMessage="Your subscription"
                      />
                    </h2>
                    <p className="small mb-2">
                      <strong>
                        <FormattedMessage id="subscription_status_label" defaultMessage="Account status" />:
                      </strong>{" "}
                      {subscriptionUserStatusLabel(displayStatus)}
                    </p>
                    {currentPlan && (
                      <p className="small mb-2">
                        <strong>
                          <FormattedMessage id="subscription_plan_label" defaultMessage="Plan" />:
                        </strong>{" "}
                        {currentPlan.name}
                        {currentPlan.price != null && (
                          <>
                            {" "}
                            (€{Number(currentPlan.price).toFixed(2)})
                          </>
                        )}
                      </p>
                    )}
                    <p className="small mb-2">
                      <strong>
                        <FormattedMessage id="subscription_started_label" defaultMessage="Started" />:
                      </strong>{" "}
                      {formatDt(salonSub.offline_subscription_started_at)}
                    </p>
                    <p className="small mb-0">
                      <strong>
                        <FormattedMessage id="subscription_expires_label" defaultMessage="Expires" />:
                      </strong>{" "}
                      {formatDt(salonSub.offline_subscription_expires_at)}
                    </p>
                    {pendingRequest && !hasPendingUpgrade && (
                      <div className="alert alert-light border mt-3 mb-0 small">
                        <strong>
                          <FormattedMessage
                            id="subscription_pending_request_title"
                            defaultMessage="Payment under review"
                          />
                        </strong>
                        <p className="mb-1 mt-2">
                          <FormattedMessage id="transaction_id" defaultMessage="Transaction ID" />:{" "}
                          {pendingRequest.transaction_id}
                        </p>
                        {pendingRequest.requested_plan && (
                          <p className="mb-1">
                            <FormattedMessage
                              id="subscription_requested_plan"
                              defaultMessage="Requested plan"
                            />
                            : {pendingRequest.requested_plan.name}
                          </p>
                        )}
                        <p className="mb-1">
                          <FormattedMessage
                            id="subscription_request_status"
                            defaultMessage="Status"
                          />
                          :{" "}
                          {pendingRequest.status_label ||
                            pendingRequest.status ||
                            "Pending"}
                        </p>
                        
                      </div>
                    )}
                  </div>
                </div>
              )}

              {pendingVerify && !hasPendingUpgrade && (
                <div className="alert alert-info" role="alert">
                  <FormattedMessage
                    id="subscription_pending_verify"
                    defaultMessage="Your payment proof is pending admin verification. You will get access after approval."
                  />
                </div>
              )}
              {!loadError && plans.length === 0 && (
                <p className="text-muted mb-4">
                  <FormattedMessage
                    id="subscription_plans_empty"
                    defaultMessage="No subscription plans are available yet. Please contact support."
                  />
                </p>
              )}
              <div className="row g-4">
                {plans.map((p: any) => {
                  const btn = planButtonState(p);
                  return (
                    <div key={p.id} className="col-12 col-md-6">
                      <div className="card h-100 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <h5 className="card-title">{p.name}</h5>
                          <div className="flex-grow-1 d-flex flex-column">
                            <p className="text-muted small">{p.overview}</p>
                            <p className="mb-1">
                              <strong>
                                <FormattedMessage id="price" defaultMessage="Price" />:{" "}
                              </strong>
                              {Number(p.price).toFixed(2)}
                            </p>
                            <p className="mb-2">
                              <strong>
                                <FormattedMessage id="validity" defaultMessage="Validity" />:{" "}
                              </strong>
                              {p.validity_days}{" "}
                              <FormattedMessage id="days" defaultMessage="days" />
                            </p>
                            <SubscriptionPlanFeaturesList
                              plan={p}
                              titleClassName="h6 mb-2 mt-1"
                              listClassName="mb-0"
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-fill mt-auto pt-3"
                            disabled={btn.disabled}
                            onClick={() => goPurchase(p.id)}
                          >
                            <FormattedMessage id={btn.labelId} defaultMessage={btn.labelDefault} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {!loadError && txHistory.length > 0 && (
                <div className="mt-5">
                  <h2 className="h6 mb-3">
                    <FormattedMessage
                      id="subscription_tx_history_title"
                      defaultMessage="Transaction history"
                    />
                  </h2>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle bg-white">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <FormattedMessage id="subscription_tx_plan" defaultMessage="Plan" />
                          </th>
                          <th>
                            <FormattedMessage id="transaction_id" defaultMessage="Transaction ID" />
                          </th>
                          <th>
                            <FormattedMessage id="subscription_tx_status" defaultMessage="Status" />
                          </th>
                          <th>
                            <FormattedMessage id="subscription_tx_submitted" defaultMessage="Submitted" />
                          </th>
                          <th>
                            <FormattedMessage id="subscription_tx_reviewed" defaultMessage="Reviewed" />
                          </th>
                          <th>
                            <FormattedMessage id="subscription_tx_remark" defaultMessage="Remark" />
                          </th>
                          <th>
                            <FormattedMessage id="subscription_tx_proof" defaultMessage="Proof" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {txHistory.map((row: any) => (
                          <tr key={row.id}>
                            <td>{row.plan?.name || "—"}</td>
                            <td>{row.transaction_id}</td>
                            <td>{row.status_label || row.status}</td>
                            <td>{formatDt(row.created_at)}</td>
                            <td>{formatDt(row.reviewed_at)}</td>
                            <td className="small">{row.admin_remark || "—"}</td>
                            <td>
                              {row.payment_proof_url ? (
                                <a href={row.payment_proof_url} target="_blank" rel="noreferrer">
                                  <FormattedMessage id="view" defaultMessage="View" />
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <Link to={Routes.Dashboard} className="btn btn-outline-secondary">
                  <FormattedMessage id="back" defaultMessage="Back" />
                </Link>
              </div>
            </div>
            <aside className="col-12 col-lg-4">
              <div className="card shadow-sm border-0 bg-light sticky-lg-top subscription-bank-sidebar">
                <div className="card-body">
                  <h2 className="h6 card-title">
                    <FormattedMessage
                      id="subscription_bank_sidebar_title"
                      defaultMessage="Pay by bank transfer"
                    />
                  </h2>
                  <p className="small text-muted">
                    <FormattedMessage
                      id="subscription_bank_sidebar_intro"
                      defaultMessage="Use these details when you pay for your plan. You will confirm the transaction on the next step."
                    />
                  </p>
                  <SubscriptionBankSummary bank={bank} />
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </div>
  );
};

export default SubscriptionPlans;
